import { z } from "zod";
import type { ToolDescription } from "../tool.js";

export const projectIdDescription = `
This is a short ID (a number) for a project.

Pass this option unless explicitly instructed not to.
`;

const teamIdDescription = `
The numeric identifier for a team. This is only obtainable by using the listTeams tool.
`;

const teamSlugDescription = `
The team slug (short name) for a team. This is obtainable from the listTeams tool.
`;

const projectNameDescription = `
The name for the new project. This should be a descriptive name for the project.
`;

export const toolDescriptions = {
  projectStatus: {
    name: "projectStatus",
    debugOnly: false,
    readonly: true,
    description: `Get all available deployments for a given Convex project.

Use the listTeams and listProjects tools to get a project ID.
Once you have the identifier for a deployment, pass it to other tools to target deployments.

Projects have a development ({"kind": "ownDev"}) and production ({"kind": "prod"}) deployment.
Generally default to using the development deployment unless you'd specifically asked to debug
issues in production.
`.trim(),
    inputSchema: z.object({
      projectId: z.number().describe(projectIdDescription),
    }),
  },
  listTeams: {
    name: "listTeams",
    debugOnly: false,
    readonly: true,
    description: `List all teams for the current user. Listed teams will include the team ID, which is required for the listProjects tool.`,
    inputSchema: z.object({}),
  },
  listProjects: {
    name: "listProjects",
    debugOnly: false,
    readonly: true,
    description: `List all projects for the current user. Pass the team ID to this tool to list projects for a specific team. The projects returned will included a project ID, which is required for the projectStatus tool.`,
    inputSchema: z.object({
      teamId: z.number().describe(teamIdDescription),
    }),
  },
  createProjectInTeam: {
    name: "createProjectInTeam",
    debugOnly: false,
    readonly: false,
    description: `Create a new Convex project in a specific team.

This tool creates a new project with the specified name in the given team. The team slug
can be obtained from the listTeams tool. Once created, the project will have a development
deployment automatically provisioned.

Returns both an admin key (for full administrative access) and a project key (for API access).`,
    inputSchema: z.object({
      teamSlug: z.string().describe(teamSlugDescription),
      projectName: z.string().describe(projectNameDescription),
    }),
  },
  // These two just for testing with builder.io atm
  readNotes: {
    name: "readNotes",
    debugOnly: false,
    readonly: true,
    description: `Read all notes written in this session.`,
    inputSchema: z.object({}),
  },
  writeNote: {
    name: "writeNote",
    debugOnly: false,
    readonly: false,
    description: `Write a note you can reference later in this session`,
    inputSchema: z.object({
      text: z.string().describe(`A string of text you can reference later.`),
    }),
  },
  // end testing tools
} satisfies Record<string, ToolDescription>;
